/**
 * 仪表板（旧 views/dashboard/DashboardView.vue）：关键指标、用户增长趋势、等级分布、实时动态
 */
import { useMemo, useState } from 'react'
import { App, Button, Card, Col, Divider, Empty, Radio, Row, Timeline, Typography, theme } from 'antd'
import {
  ClockCircleOutlined,
  GlobalOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/PageHeader'
import { EChart } from '@/components/charts/EChart'
import { buildDonutOption, buildTrendLineOption } from '@/components/charts/presets'
import { useAppliedTheme } from '@/stores/theme'
import { getChartPalette } from '@/theme/chartTheme'
import { formatStudyDuration } from '@/utils/format'
import { StatsCard } from './StatsCard'
import { DASHBOARD_KEY, useLevelDistribution, useOverviewStats, useRealtime, useTrends, type RecentActivity } from './hooks'
import styles from './DashboardPage.module.css'

const CHART_HEIGHT = 350

const TREND_RANGE_OPTIONS = [
  { label: '7天', value: 7 },
  { label: '30天', value: 30 },
  { label: '90天', value: 90 }
]

// 时间线节点颜色用 antd 预设色名
const ACTIVITY_COLORS: Record<RecentActivity['type'], string> = {
  registration: 'blue',
  learning: 'green'
}

function TrendCard() {
  const [days, setDays] = useState(7)
  const { data } = useTrends(days)
  const hasData = !!data && data.dates.length > 0
  const option = useMemo(() => (data ? buildTrendLineOption(data.dates, data.series) : null), [data])
  return (
    <Card
      variant="borderless"
      title="用户增长趋势"
      extra={
        <Radio.Group
          aria-label="趋势时间范围"
          size="small"
          optionType="button"
          buttonStyle="solid"
          options={TREND_RANGE_OPTIONS}
          value={days}
          onChange={(event) => setDays(event.target.value)}
        />
      }
    >
      {hasData && option ? (
        <EChart option={option} height={CHART_HEIGHT} ariaLabel="用户增长趋势" />
      ) : (
        <Empty className={styles.empty} description="暂无数据" />
      )}
    </Card>
  )
}

function LevelDistributionCard() {
  const { data } = useLevelDistribution()
  // 全部为 0 时饼图画不出扇区，按空数据处理
  const hasData = !!data && data.some((item) => item.value > 0)
  const option = useMemo(() => buildDonutOption(data ?? []), [data])
  return (
    <Card variant="borderless" title="用户等级分布">
      {hasData ? (
        <EChart option={option} height={CHART_HEIGHT} ariaLabel="用户等级分布" />
      ) : (
        <Empty className={styles.empty} description="暂无数据" />
      )}
    </Card>
  )
}

function RealtimeCard() {
  const { token } = theme.useToken()
  const { data } = useRealtime()
  const activities = data?.recentActivities ?? []
  return (
    <Card variant="borderless" title="实时动态">
      <div className={styles.todayActive}>
        <GlobalOutlined style={{ color: token.colorSuccess }} />
        <span>
          今日活跃：<strong className={styles.todayActiveValue}>{data?.todayActiveUsers ?? 0}</strong>
        </span>
      </div>
      <Divider />
      <Typography.Text type="secondary" className={styles.sectionTitle}>
        最近活动
      </Typography.Text>
      {activities.length > 0 ? (
        <Timeline
          items={activities.map((activity, index) => ({
            key: `${activity.user}-${index}`,
            color: ACTIVITY_COLORS[activity.type],
            content: (
              <>
                <Typography.Text type="secondary" className={styles.activityTime}>
                  {activity.time}
                </Typography.Text>
                <Typography.Text strong className={styles.activityUser}>
                  {activity.user}
                </Typography.Text>
                <span>{activity.detail}</span>
              </>
            )
          }))}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无活动" />
      )}
    </Card>
  )
}

export function DashboardPage() {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const appliedTheme = useAppliedTheme()
  const palette = getChartPalette(appliedTheme)
  const overview = useOverviewStats()
  const [refreshing, setRefreshing] = useState(false)
  const stats = overview.data
  const statsLoading = overview.isPending || refreshing

  // 刷新页面上全部数据；趋势图按当前选中的天数重新请求
  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await queryClient.refetchQueries({ queryKey: DASHBOARD_KEY, type: 'active' })
      const failed = queryClient
        .getQueryCache()
        .findAll({ queryKey: DASHBOARD_KEY, type: 'active' })
        .some((query) => query.state.status === 'error')
      if (failed) message.error('数据刷新失败')
      else message.success('数据刷新成功')
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="仪表板"
        extra={
          <Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>
            刷新数据
          </Button>
        }
      />

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard
            title="用户总数"
            value={stats?.totalUsers ?? 0}
            icon={<UserOutlined />}
            accentColor={palette[0]}
            trend={stats?.growthRate ?? 0}
            loading={statsLoading}
            description="平台注册用户总数"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard
            title="今日新增"
            value={stats?.todayNewUsers ?? 0}
            icon={<UserAddOutlined />}
            accentColor={palette[1]}
            loading={statsLoading}
            description="今日新注册用户"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard
            title="活跃用户"
            value={stats?.activeUsers ?? 0}
            icon={<TeamOutlined />}
            accentColor={palette[2]}
            loading={statsLoading}
            description="7天内活跃用户"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard
            title="学习总时长"
            value={formatStudyDuration(stats?.totalLearningSeconds)}
            icon={<ClockCircleOutlined />}
            accentColor={palette[4]}
            loading={statsLoading}
            description="累计学习时长"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={16}>
          <TrendCard />
        </Col>
        <Col xs={24} lg={8}>
          <LevelDistributionCard />
        </Col>
      </Row>

      <RealtimeCard />
    </div>
  )
}
